import { useTranslation } from 'react-i18next';
import {
  Link as RouterLink,
  useHistory,
  useLocation,
} from 'react-router-dom';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import {
  Box,
  Breadcrumbs,
  IconButton,
  Link,
  Tooltip,
  Typography,
} from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import NavigateNextIcon from '@material-ui/icons/NavigateNext';
import { invalidRoutes } from 'constants/invalidRoutes';

import useStyles from './Breadcrumb.styles';

function Breadcrumb({
  navigationActions,
  pieces,
}) {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const { i18n, t } = useTranslation('translation', {
    keyPrefix: 'components.breadcrumb',
  });

  const pathParts = pieces
    ? ['', ...pieces]
    : location.pathname.split('/');

  const navigationConfig = navigationActions && typeof navigationActions === 'object'
    ? navigationActions
    : {};
  const showNavigationActions = navigationActions !== false
    && navigationConfig.visible !== false;
  const backAction = navigationConfig.back === false
    ? {
      hidden: true,
    }
    : navigationConfig.back || {};

  const handleNavigationAction = (action) => {
    if (typeof action.onClick === 'function') {
      action.onClick({
        direction: 'back',
        history,
      });
      return;
    }

    if (typeof action.fn === 'function') {
      action.fn({
        direction: 'back',
        history,
      });
      return;
    }

    history.goBack();
  };

  const renderNavigationButton = (action) => {
    if (action.hidden) {
      return null;
    }

    const label = action.label || t('go_back');

    return (
      <Tooltip title={action.tooltip || label}>
        <span>
          <IconButton
            aria-label={label}
            className={classes.navigationButton}
            disabled={action.disabled}
            onClick={() => handleNavigationAction(action)}
            size="small"
          >
            {action.icon || <ArrowBackIcon fontSize="small" />}
          </IconButton>
        </span>
      </Tooltip>
    );
  };

  return (
    <Box className={classes.root}>
      {showNavigationActions && (
        <Box className={classes.navigationActions}>
          {renderNavigationButton(backAction)}
        </Box>
      )}

      <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
        {pathParts.slice(1).map((item, index) => {
          let [label] = Array.isArray(item)
            ? item
            : [item];
          label = i18n.exists(`components.breadcrumb.${label}`) ?
            t(label)
            : label.includes('.') && i18n.exists(`components.breadcrumb.${label.replace('.', '_')}`)
              ? t(label.replace('.', '_'))
              : label;

          let to = pathParts
            .slice(0, index + 2)
            .map((part) => Array.isArray(part) ? part[1] : part);

          to = to
            .map((_) => to.length > 2 ? _.split('?')[0] : _)
            .join('/');


          if(invalidRoutes.includes( to ) || (pathParts[1] === 'pld' && index < 1)) {
            return (
              <Typography
                children={label}
                className={clsx(classes.text)}
                color="textPrimary"
                key={index}
                variant="body1"
              />
            );
          }

          return ![pathParts.length - 2].includes(index)
            ? (<Link
              children={label}
              className={clsx(classes.text)}
              color="inherit"
              component={RouterLink}
              key={index}
              to={to}
              variant="body1"
            />)
            : (<Typography
              children={label}
              className={clsx(classes.text)}
              color="textPrimary"
              key={index}
              variant="body1"
            />);
        })}
      </Breadcrumbs>
    </Box>
  );
}

Breadcrumb.defaultProps = {
  navigationActions: true,
};

Breadcrumb.propTypes = {
  navigationActions: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.shape({
      back: PropTypes.oneOfType([
        PropTypes.bool,
        PropTypes.shape({
          disabled: PropTypes.bool,
          fn: PropTypes.func,
          hidden: PropTypes.bool,
          icon: PropTypes.element,
          label: PropTypes.string,
          onClick: PropTypes.func,
          tooltip: PropTypes.string,
        }),
      ]),
      visible: PropTypes.bool,
    }),
  ]),
  pieces: PropTypes.array,
};

export default Breadcrumb;
